/** Map complete CSS url() values without rewriting comments or string literals.
 * Self-contained so the same tokenizer can run in the sandbox via toString().
 * Resolvers receive decoded values; unchanged URLs retain their exact source text.
 */
export function mapCssAssetUrls(
  css: string,
  resolve: (url: string) => string,
): string {
  const space = /[\t\n\f\r ]/;
  const identifier = /[-_a-z\d\u0080-\uffff]/i;
  let index = 0;
  let copied = 0;
  let result = "";
  const escape = (
    start: number,
  ): { end: number; value: string; newline: boolean } => {
    let cursor = start + 1;
    const character = css[cursor];
    if (character === undefined)
      return { end: cursor, value: "\ufffd", newline: false };
    if (/[\n\r\f]/.test(character)) {
      cursor += character === "\r" && css[cursor + 1] === "\n" ? 2 : 1;
      return { end: cursor, value: "", newline: true };
    }
    const hexStart = cursor;
    while (cursor - hexStart < 6 && /[a-f\d]/i.test(css[cursor] ?? ""))
      cursor++;
    if (cursor !== hexStart) {
      const code = parseInt(css.slice(hexStart, cursor), 16);
      if (space.test(css[cursor] ?? ""))
        cursor += css[cursor] === "\r" && css[cursor + 1] === "\n" ? 2 : 1;
      return {
        end: cursor,
        value:
          code === 0 || code > 0x10ffff || (code >= 0xd800 && code <= 0xdfff)
            ? "\ufffd"
            : String.fromCodePoint(code),
        newline: false,
      };
    }
    return { end: cursor + 1, value: character, newline: false };
  };
  const skipString = (start: number): number => {
    const quote = css[start];
    let cursor = start + 1;
    while (cursor < css.length) {
      if (css[cursor] === "\\") cursor += 2;
      else if (css[cursor++] === quote) return cursor;
    }
    return css.length;
  };
  while (index < css.length) {
    if (css.startsWith("/*", index)) {
      const end = css.indexOf("*/", index + 2);
      index = end < 0 ? css.length : end + 2;
      continue;
    }
    if (css[index] === '"' || css[index] === "'") {
      index = skipString(index);
      continue;
    }
    if (!identifier.test(css[index]!) && css[index] !== "\\") {
      index++;
      continue;
    }
    let name = "";
    let validName = true;
    while (index < css.length) {
      if (css[index] === "\\") {
        const escaped = escape(index);
        name += escaped.value;
        validName &&= !escaped.newline;
        index = escaped.end;
      } else if (identifier.test(css[index]!)) name += css[index++];
      else break;
    }
    if (!validName || name.toLowerCase() !== "url" || css[index] !== "(")
      continue;
    index++;
    while (space.test(css[index] ?? "")) index++;
    const quote = css[index] === '"' || css[index] === "'" ? css[index++]! : "";
    const start = index;
    let end = index;
    let source = "";
    let valid = true;
    if (quote) {
      while (index < css.length && css[index] !== quote) {
        if (css[index] === "\\") {
          const escaped = escape(index);
          source += escaped.value;
          index = escaped.end;
        } else {
          if (/[\n\r\f]/.test(css[index]!)) valid = false;
          source += css[index++];
        }
      }
      end = index;
      if (css[index] !== quote) valid = false;
      else index++;
      while (index < css.length) {
        if (space.test(css[index]!)) index++;
        else if (css.startsWith("/*", index)) {
          const end = css.indexOf("*/", index + 2);
          index = end < 0 ? css.length : end + 2;
        } else break;
      }
      if (css[index] !== ")") valid = false;
    } else {
      while (index < css.length && css[index] !== ")") {
        const character = css[index]!;
        if (character === "\\") {
          const escaped = escape(index);
          source += escaped.value;
          valid &&= !escaped.newline;
          index = escaped.end;
        } else if (space.test(character)) {
          end = index;
          while (space.test(css[index] ?? "")) index++;
          if (css[index] !== ")") valid = false;
          break;
        } else {
          if (
            /["'(]/.test(character) ||
            character.charCodeAt(0) < 32 ||
            character.charCodeAt(0) === 127
          )
            valid = false;
          source += character;
          index++;
        }
      }
      if (end === start) end = index;
    }
    if (css[index] !== ")") {
      // A bad URL is one token; do not reinterpret any nested text in it.
      while (index < css.length && css[index] !== ")") index++;
      valid = false;
    }
    if (index < css.length) index++;
    if (!valid || end <= start) continue;
    const resolved = resolve(source);
    if (resolved === source) continue;
    const encoded = Array.from(resolved, (character) => {
      const code = character.charCodeAt(0);
      const unsafe =
        character === "\\" ||
        code < 32 ||
        code === 127 ||
        (quote ? character === quote : /[\s"'()]/.test(character));
      return unsafe ? `\\${code.toString(16)} ` : character;
    }).join("");
    result += css.slice(copied, start) + encoded;
    copied = end;
  }
  return result + css.slice(copied);
}
